// react
import { useCallback, useEffect, useMemo, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// feature-hooks
import useChartHighlightParams from '@/features/energyxplorer/hooks/useChartHighlightParams';
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';
import useResponsiveChartHeight from '@/features/energyxplorer/hooks/useResponsiveChartHeight';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/no-data-to-display';

const getYear = (date) => new Date(date).getFullYear();

const getWeekNumber = (date) => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7; // Monday=0
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  const firstWeekDay = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - firstWeekDay + 3);
  const firstThursdayOfYear = target.valueOf();

  return (
    1 +
    Math.round((firstThursday - firstThursdayOfYear) / (7 * 24 * 3600 * 1000))
  );
};

const formatTimestamp = (timestamp, aggregate) => {
  const date = new Date(timestamp);

  switch (aggregate) {
    case 'hour':
      return date.toLocaleString('da-DK', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

    case 'day':
      return date.toLocaleDateString('da-DK', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });

    case 'week': {
      const weekNumber = getWeekNumber(date);
      const year = getYear(date);
      return `uge ${weekNumber} ${year}`;
    }

    case 'month':
      return date.toLocaleDateString('da-DK', { month: 'short' });

    case 'year':
      return date.toLocaleDateString('da-DK', { year: 'numeric' });

    default:
      return date.toLocaleString('da-DK');
  }
};

const EnergyAnalysisSingleSeriesChart = ({
  availableSeriesIds,
  lazyLoadedEnergyConsumptionData,
  isLoading,
  viewMode,
  aggregate,
  dataSeriesAggregate,
  setSeriesColors,
}) => {
  const chartComponentRef = useRef(null);
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const theme = useTheme();
  const chartHeight = useResponsiveChartHeight();
  const { unit } = useEnergyGroupUnit();

  const { highlightEnabled, plotBands, onChartSelection } =
    useChartHighlightParams();

  const themeOptions = useMemo(
    () => (theme.palette.mode === 'dark' ? darkTheme : lightTheme),
    [theme.palette.mode],
  );

  const generateLegendName = useCallback(
    (series) => {
      const getFilterLabel = (type, value) => {
        switch (type) {
          case 'activePeriodFilter':
            switch (value) {
              case true:
                return t('filter_labels.active_period');
              case false:
                return t('filter_labels.inactive_period');
              default:
                return t('filter_labels.all_periods');
            }
          case 'activeHolidaysFilter':
            switch (value) {
              case true:
                return t('filter_labels.holidays_included');
              case false:
                return t('filter_labels.holidays_excluded');
              default:
                return '';
            }
          default:
            return '';
        }
      };

      if (!series || !series.metadata) return '';

      const { activePeriodFilter, activeHolidaysFilter } =
        series.metadata || {};

      const parts = [];

      parts.push(getFilterLabel('activePeriodFilter', activePeriodFilter));

      const holidayLabel = getFilterLabel(
        'activeHolidaysFilter',
        activeHolidaysFilter,
      );

      if (holidayLabel) parts.push(holidayLabel);

      return parts.length > 0 ? parts.join(', ') : '';
    },
    [t],
  );

  // Imperative loading overlay (do NOT bake this into options)
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
    if (isLoading) {
      chart.showLoading(`${t('common.loading_data')}`);
      chart.hideNoData();
    } else {
      chart.hideLoading();
    }
  }, [isLoading, t]);

  const uniqueTimestamps = useMemo(() => {
    if (
      !lazyLoadedEnergyConsumptionData ||
      lazyLoadedEnergyConsumptionData[0] === null
    ) {
      return [];
    }

    const ts = new Set();
    lazyLoadedEnergyConsumptionData.forEach((series) => {
      (series?.EnergyData || []).forEach((item) => {
        ts.add(new Date(item.timestamp).getTime());
      });
    });

    return Array.from(ts).sort((a, b) => a - b);
  }, [lazyLoadedEnergyConsumptionData]);

  const chartSeries = useMemo(() => {
    if (
      !lazyLoadedEnergyConsumptionData ||
      lazyLoadedEnergyConsumptionData[0] === null
    ) {
      return [];
    }

    const valueKey = dataSeriesAggregate?.value;
    if (!valueKey) return [];

    return lazyLoadedEnergyConsumptionData.map((series) => {
      const points = series?.EnergyData || [];

      // Index for O(1) lookup by timestamp
      const byTs = new Map();
      points.forEach((p) => byTs.set(new Date(p.timestamp).getTime(), p));

      const baseName = series?.EnergySummary?.Name || '';
      const metadata =
        series?.metadata || series?.EnergySummary?.metadata || {}; // adjust if your metadata lives elsewhere

      // Important: generateLegendName expects { metadata: ... }
      const legendLabel = generateLegendName({ metadata });

      return {
        name: legendLabel ? `${baseName} (${legendLabel})` : baseName,
        baseName, // keep original available for tooltips/exports if needed
        metadata, // keep on the series so the callback has data
        type: viewMode === 'line' ? 'line' : 'column',
        data: uniqueTimestamps.map((ts) => {
          const p = byTs.get(ts);
          const v = p ? p[valueKey] : undefined;
          return [ts, v !== undefined ? v : null];
        }),
      };
    });
  }, [
    lazyLoadedEnergyConsumptionData,
    dataSeriesAggregate?.value,
    uniqueTimestamps,
    viewMode,
    generateLegendName,
  ]);

  const xAxisLabelFormatter = useCallback(
    function () {
      return formatTimestamp(this.value, aggregate);
    },
    [aggregate],
  );

  const tooltipFormatter = useCallback(
    function () {
      let tooltip = ``;

      (this.points || []).forEach((point) => {
        tooltip += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${
          point.color
        };margin-right:5px;"></span>${point.series.name}: <b>${formatNumber(
          point.y,
        )} ${unit}</b>
        <span style="font-size:12px;color:gray;">(${formatTimestamp(
          point.x,
          aggregate,
        )})</span><br/>`;
      });

      return tooltip;
    },
    [aggregate, unit],
  );

  const options = useMemo(() => {
    return {
      lang: {
        noData: ' ',
      },
      noData: {
        style: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'lightgray' : 'gray',
        },
      },
      loading: {
        labelStyle: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        style: {
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        showDuration: 150,
        hideDuration: 150,
      },
      chart: {
        type: viewMode === 'line' ? 'line' : 'column',
        style: { cursor: highlightEnabled ? 'crosshair' : 'default' },
        height: chartHeight,
        zooming: { type: 'x', mouseWheel: { enabled: false } },
        events: {
          selection: onChartSelection,
          load() {
            if (isLoading) this.showLoading('Loading data…');
            const chart = this;
            if (chart.__mouseleaveHandler) return;

            chart.__mouseleaveHandler = () => {
              // Tooltip
              chart.tooltip?.hide(0);

              // Clear hovered points (shared tooltip can use hoverPoints)
              if (chart.hoverPoints?.length) {
                chart.hoverPoints.forEach((p) => p.setState(''));
                chart.hoverPoints.length = 0;
              }

              // Clear single hovered point
              if (chart.hoverPoint) {
                chart.hoverPoint.setState('');
                chart.hoverPoint = null;
              }

              // Clear hovered series (removes “glow/thickening” highlight)
              if (chart.hoverSeries) {
                chart.hoverSeries.setState('');
                chart.hoverSeries = null;
              }

              // Clear series/point states (also removes inactive dimming)
              chart.series.forEach((s) => {
                s.setState('');
                // Points can keep state in some cases (markers/halos)
                if (s.points) s.points.forEach((p) => p.setState(''));
              });

              // Reset pointer + crosshairs
              chart.pointer?.reset?.(true);
              chart.xAxis?.forEach((a) => a.hideCrosshair?.());
              chart.yAxis?.forEach((a) => a.hideCrosshair?.());
            };

            chart.container.addEventListener(
              'mouseleave',
              chart.__mouseleaveHandler,
            );
          },
          destroy() {
            const chart = this;
            if (chart.__mouseleaveHandler) {
              chart.container.removeEventListener(
                'mouseleave',
                chart.__mouseleaveHandler,
              );
              chart.__mouseleaveHandler = null;
            }
          },
        },
        spacingLeft: 40,
        spacingRight: 40,
        backgroundColor: themeOptions.chart.backgroundColor,
      },
      time: { useUTC: false },
      credits: { enabled: false },
      title: { text: '' },

      xAxis: {
        type: 'datetime',
        plotBands,
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        lineColor: themeOptions.xAxis.lineColor,
        labels: {
          formatter: xAxisLabelFormatter,
          style: {
            color: themeOptions.xAxis.labels.style.color,
            whiteSpace: 'normal',
            width: '30px',
            textAlign: 'center',
          },
        },
      },

      yAxis: {
        min: 0,
        title: { text: `Energiforbrug (${unit})` },
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        labels: { style: { color: themeOptions.yAxis.labels.style.color } },
      },

      legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },

      tooltip: {
        shared: true,
        useHTML: true,
        formatter: tooltipFormatter,
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: { color: theme.palette.mode === 'dark' ? 'white' : 'black' },
      },

      plotOptions: {
        line: { marker: { enabled: false } },
        column: { grouping: true, borderColor: 'transparent' },
      },

      series: chartSeries,
    };
  }, [
    chartHeight,
    chartSeries,
    highlightEnabled,
    isLoading,
    onChartSelection,
    plotBands,
    theme.palette.mode,
    themeOptions.chart.backgroundColor,
    themeOptions.legend.itemStyle.color,
    themeOptions.tooltip.backgroundColor,
    themeOptions.tooltip.borderColor,
    themeOptions.xAxis.labels.style.color,
    themeOptions.xAxis.lineColor,
    themeOptions.yAxis.labels.style.color,
    tooltipFormatter,
    unit,
    viewMode,
    xAxisLabelFormatter,
  ]);

  useEffect(() => {
    if (typeof setSeriesColors !== 'function') return;
    if (!availableSeriesIds?.size) return;

    const chart = chartComponentRef.current?.chart;
    if (!chart || !chart.series?.length) return;

    // Sort the indices that actually have data (0,2,...) so mapping is stable.
    const indicesWithData = Array.from(availableSeriesIds).sort(
      (a, b) => a - b,
    );

    // Build an array aligned to filterObjects indices:
    // seriesColorsByIndex[filterIndex] = { name, color }
    const seriesColorsByIndex = [];

    indicesWithData.forEach((filterIndex, producedSeriesPos) => {
      const s = chart.series[producedSeriesPos];
      if (!s) return;
      seriesColorsByIndex[filterIndex] = { name: s.name, color: s.color };
    });

    setSeriesColors(seriesColorsByIndex);
  }, [availableSeriesIds, chartSeries, setSeriesColors]);

  return (
    <>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
      />
    </>
  );
};

export default EnergyAnalysisSingleSeriesChart;
